import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addHours, format, isBefore, parseISO, startOfDay } from 'date-fns';
import { Form, Formik } from 'formik';
import { FC } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { useSession } from 'src/contexts/SessionContext';
import { ScheduleItem } from 'src/models/schedule/_types';
import {
  createSchedule,
  deleteSchedule,
  updateSchedule,
} from 'src/models/schedule/schedule';
import {
  FormValues,
  ManageSchedule,
} from 'src/shared/pageComponents/Schedules/ManageSchedule';
import api from 'src/utils/ajax';
import * as Yup from 'yup';

import type { Identifier } from 'src/types/utilityTypes';

interface UnavailableDialogProps {
  open: boolean;
  onClose: () => void;
  /** Existing schedule to edit. If null/undefined, dialog is in "create" mode */
  schedule?: ScheduleItem | null;
  /** Initial date for new events (create mode only) */
  initialDate?: Date | null;
  /** Initial trainer ID for new events (create mode only) */
  initialTrainerId?: Identifier;
  onSaveSuccess?: (schedule: ScheduleItem) => void;
  onDeleteSuccess?: () => void;
}

/**
 * UnavailableDialog - Create or edit a schedule (unavailable/availability block)
 *
 * Combines functionality from:
 * - CreateUnavailableDialog (create mode)
 * - ViewUnavailableDialog (edit mode)
 *
 * Migrated from v2/app/controllers/dashboard/fbm/modal-edit-schedule.js
 */
export const UnavailableDialog: FC<UnavailableDialogProps> = ({
  open,
  onClose,
  schedule,
  initialDate,
  initialTrainerId,
  onSaveSuccess,
  onDeleteSuccess,
}) => {
  const { t: tButtons } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'schedules.newSchedule',
  });

  const queryClient = useQueryClient();
  const { user } = useSession();

  const [confirmDialogProps, confirm] = useConfirmDialogComponent();

  // Determine if we're editing an existing schedule or creating a new one
  const isEditing = !!schedule;

  const createScheduleMutation = useMutation({
    mutationFn: (data: Partial<ScheduleItem>) => createSchedule(data),
    onSuccess: (createdSchedule) => {
      toast.success(t('createSuccess'));
      invalidateQueries();
      onSaveSuccess?.(createdSchedule);
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error));
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: (data: Partial<ScheduleItem>) =>
      updateSchedule(schedule!.id, data),
    onSuccess: (updatedSchedule) => {
      toast.success(t('updateSuccess'));
      invalidateQueries();
      onSaveSuccess?.(updatedSchedule);
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error));
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: () => deleteSchedule(schedule!.id),
    onSuccess: () => {
      toast.success(t('deleteSuccess'));
      invalidateQueries();
      onDeleteSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error));
    },
  });

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['schedules'] });
    queryClient.invalidateQueries({ queryKey: ['calendar'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-schedules'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  const validationSchema = Yup.object().shape({
    scheduleType: Yup.string().required(),
    startDate: Yup.string().required(),
    startTime: Yup.string().required(),
    endTime: Yup.string().required(),
    intervalInMinutes: Yup.number()
      .nullable()
      .when(['scheduleType'], {
        is: (value: string) => value === 'available',
        then: () => Yup.number().nullable().min(0).integer(),
      }),
    bufferBeforeInMinutes: Yup.number().nullable().min(0).integer(),
    bufferInMinutes: Yup.number().nullable().min(0).integer(),
  });

  // Build initial values based on mode (create vs edit)
  const getInitialValues = (): FormValues => {
    if (isEditing && schedule) {
      // Edit mode: populate from existing schedule
      return {
        name: schedule.name || '',
        scheduleType: schedule.scheduleType || 'unavailable',
        startDate: schedule.startDate || format(new Date(), 'yyyy-MM-dd'),
        endDate: schedule.endDate || '',
        days: schedule.days || [],
        recurringCount: schedule.recurringCount || 1,
        recurringType:
          (schedule.recurringType as FormValues['recurringType']) || 'week',
        recurringDayNumbers: schedule.recurringDayNumbers || [],
        noTrainers: schedule.noTrainers || false,
        trainerId: schedule.trainerId ? String(schedule.trainerId) : '',
        subTrainerId: schedule.subTrainerId
          ? String(schedule.subTrainerId)
          : '',
        startTime: schedule.startTime || '09:00',
        endTime: schedule.endTime || '10:00',
        locationId: schedule.locationId ? String(schedule.locationId) : '',
        serviceIds: schedule.serviceIds || [],
        blockAvailable: schedule.blockAvailable ?? true,
        intervalInMinutes: schedule.intervalInMinutes ?? null,
        bufferBeforeInMinutes: schedule.bufferBeforeInMinutes
          ? String(schedule.bufferBeforeInMinutes)
          : '',
        bufferInMinutes: schedule.bufferInMinutes
          ? String(schedule.bufferInMinutes)
          : '',
        color: schedule.color || '',
      };
    }

    // Create mode: populate from initial date/trainer
    const startDate = initialDate
      ? format(initialDate, 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');
    const startTime = initialDate ? format(initialDate, 'HH:mm') : '09:00';
    const endTime = initialDate
      ? format(addHours(initialDate, 1), 'HH:mm')
      : '10:00';
    const dayOfWeek = initialDate
      ? format(initialDate, 'EEEE').toLowerCase()
      : format(new Date(), 'EEEE').toLowerCase();

    return {
      name: '',
      scheduleType: 'unavailable',
      startDate,
      endDate: startDate,
      days: [dayOfWeek],
      recurringCount: 1,
      recurringType: 'week',
      recurringDayNumbers: [],
      noTrainers: false,
      trainerId: '',
      subTrainerId: initialTrainerId || user?.id || '',
      startTime,
      endTime,
      locationId: '',
      serviceIds: [],
      blockAvailable: true,
      intervalInMinutes: null,
      bufferBeforeInMinutes: '',
      bufferInMinutes: '',
      color: '',
    };
  };
  const initialValues = getInitialValues();

  const handleSubmit = async (values: FormValues) => {
    if (values.endDate) {
      const endDate = startOfDay(parseISO(values.endDate));
      const today = startOfDay(new Date());

      if (isBefore(endDate, today)) {
        const shouldProceed = await confirm({
          title: tCommon('confirm'),
          message: t('endDateBeforeToday'),
          confirmText: tButtons('yes'),
          cancelText: tButtons('no'),
        });

        if (!shouldProceed) return;
      }
    }

    if (isEditing) {
      updateScheduleMutation.mutate(values as unknown as Partial<ScheduleItem>);
    } else {
      createScheduleMutation.mutate(values as unknown as Partial<ScheduleItem>);
    }
  };

  const handleDelete = async () => {
    const shouldDelete = await confirm({
      title: tCommon('confirm'),
      message: t('deleteConfirm'),
      confirmText: tButtons('delete'),
      cancelText: tButtons('cancel'),
    });

    if (shouldDelete) {
      deleteScheduleMutation.mutate();
    }
  };

  // Determine title based on mode and schedule type
  const getTitle = () => {
    if (!isEditing) {
      return t('blockTimeAsUnavailable');
    }
    return schedule?.scheduleType === 'unavailable'
      ? t('editUnavailableTime')
      : t('editAvailabilitySchedule');
  };
  const title = getTitle();

  // Build actions based on mode
  const getActions = () => {
    const baseActions = [
      {
        label: tButtons('save'),
        onClick: () => {}, // Will be replaced by submitForm
        color: 'primary' as const,
        variant: 'contained' as const,
        loading: isEditing
          ? updateScheduleMutation.isPending
          : createScheduleMutation.isPending,
      },
    ];

    if (isEditing) {
      return [
        {
          label: tButtons('delete'),
          onClick: handleDelete,
          color: 'error' as const,
          variant: 'outlined' as const,
          loading: deleteScheduleMutation.isPending,
        },
        ...baseActions,
      ];
    }

    return baseActions;
  };
  const actions = getActions();

  return (
    <>
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm, resetForm }) => (
          <Dialog
            open={open}
            onClose={() => {
              resetForm();
              onClose();
            }}
            title={title}
            maxWidth="md"
            fullWidth
            actions={actions.map((action) =>
              action.label === tButtons('save')
                ? { ...action, onClick: submitForm }
                : action
            )}
          >
            <Form>
              <Box sx={{ py: 1 }}>
                <ManageSchedule />
              </Box>
            </Form>
          </Dialog>
        )}
      </Formik>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};

